import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { NotificationProvider } from './context/NotificationContext';
import { TimeTrackingProvider } from './context/TimeTrackingContext';
import LandingPage from './pages/LandingPage';
import Layout from './components/Layout';
import Login from './pages/Login';
import Register from './pages/Register';
import VerifyEmail from './pages/VerifyEmail';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import Dashboard from './pages/Dashboard';
import AdminDashboard from './pages/AdminDashboard';
import ManagerDashboard from './pages/ManagerDashboard';
import EmployeeDashboard from './pages/EmployeeDashboard';
import TasksPage from './pages/TasksPage';
import AdminTasksPage from './pages/admin/TasksPage';
import AdminTaskDetailPage from './pages/admin/TaskDetailPage';
import AdminTeamPage from './pages/admin/team/TeamPage';
import AdminClientsPage from './pages/admin/clients/ClientsPage';
import ProjectOverviewPage from './pages/admin/ProjectOverviewPage';
import ProjectsPage from './pages/admin/ProjectsPage';
import ProjectTasksPage from './pages/admin/ProjectTasksPage';
import EmployeeProjectTasksPage from './pages/employee/ProjectTasksPage';
import ProjectTasksDetailPage from './pages/employee/ProjectTasksDetailPage';
import TaskDetailPage from './pages/employee/TaskDetailPage';
import ManagerTasksPage from './pages/manager/TasksPage';
import ManagerProjectTasksPage from './pages/manager/ProjectTasksPage';
import ManagerProjectsPage from './pages/manager/ProjectsPage';
import ManagerTeamPage from './pages/manager/TeamPage';
import ManagerTaskDetailPage from './pages/manager/TaskDetailPage';
import ProfilePage from './pages/ProfilePage';
import SettingsPage from './pages/SettingsPage';
import UserDetailPage from './pages/admin/UserDetailPage';
import EmployeeTaskDetailPage from './pages/employee/TaskDetailPage';
import EmployeeTasksPage from './pages/employee/TasksPage';
import TimesheetPage from './pages/employee/TimesheetPage';
import OfficePolicyPage from './pages/employee/OfficePolicyPage';
import MonthlyReportPage from './pages/employee/MonthlyReportPage';
import TimesheetManagementPage from './pages/admin/TimesheetManagementPage';
import PolicyManagementPage from './pages/admin/PolicyManagementPage';
import LeaveRequestManagementPage from './pages/admin/LeaveRequestManagementPage';
import EmailTestPage from './pages/admin/EmailTestPage';
import AdminMonthlyReportPage from './pages/admin/AdminMonthlyReportPage';
import TimeTrackingPage from './pages/TimeTrackingPage';
import TeamPage from './pages/TeamPage';
import ClientsPage from './pages/ClientsPage';
import { isAuthenticated, getCurrentUser } from './services/authService';
import './index.css';

// Role-based route protection
const RoleRoute = ({ element, allowedRoles }) => {
  if (!isAuthenticated()) {
    return <Navigate to="/login" />;
  }
  
  const user = getCurrentUser();
  if (!user || !allowedRoles.includes(user.role)) {
    // Redirect to appropriate dashboard based on role
    if (user.role === 'admin') {
      return <Navigate to="/admin/dashboard" />;
    } else if (user.role === 'manager') {
      return <Navigate to="/manager/dashboard" />;
    } else {
      return <Navigate to="/employee/dashboard" />;
    }
  }
  
  return element;
};

const PrivateRoute = ({ children }) => {
  return isAuthenticated() ? children : <Navigate to="/login" />;
};

function App() {
  return (
    <Router>
      <NotificationProvider>
        <TimeTrackingProvider>
        <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/verify-email" element={<VerifyEmail />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        {/* Admin Routes */}
        <Route 
          path="/admin/dashboard" 
          element={
            <PrivateRoute>
              <Layout>
                <RoleRoute element={<AdminDashboard />} allowedRoles={['admin']} />
              </Layout>
            </PrivateRoute>
          } 
        />
        
        {/* Manager Routes */}
        <Route 
          path="/manager/dashboard" 
          element={
            <PrivateRoute>
              <Layout>
                <RoleRoute element={<ManagerDashboard />} allowedRoles={['admin', 'manager']} />
              </Layout>
            </PrivateRoute>
          } 
        />
        
        {/* Employee Routes */}
        <Route 
          path="/employee/dashboard" 
          element={
            <PrivateRoute>
              <Layout>
                <RoleRoute element={<EmployeeDashboard />} allowedRoles={['admin', 'manager', 'employee']} />
              </Layout>
            </PrivateRoute>
          } 
        />
        
        {/* Legacy dashboard route - redirects based on role */}
        <Route 
          path="/dashboard" 
          element={
            <PrivateRoute>
              <Layout>
                {getCurrentUser()?.role === 'admin' ? 
                  <Navigate to="/admin/dashboard" /> : 
                  getCurrentUser()?.role === 'manager' ? 
                    <Navigate to="/manager/dashboard" /> : 
                    <Navigate to="/employee/dashboard" />
                }
              </Layout>
            </PrivateRoute>
          } 
        />
        {/* Admin Tasks */}
        <Route 
          path="/admin/tasks" 
          element={
            <PrivateRoute>
              <Layout>
                <RoleRoute element={<AdminTasksPage />} allowedRoles={['admin']} />
              </Layout>
            </PrivateRoute>
          } 
        />
        
        {/* Admin Task Detail */}
        <Route 
          path="/admin/tasks/:taskId" 
          element={
            <PrivateRoute>
              <Layout>
                <RoleRoute element={<AdminTaskDetailPage />} allowedRoles={['admin']} />
              </Layout>
            </PrivateRoute>
          } 
        />
        
        {/* Admin Timesheet Management */}
        <Route 
          path="/admin/timesheets" 
          element={
            <PrivateRoute>
              <Layout>
                <RoleRoute element={<TimesheetManagementPage />} allowedRoles={['admin']} />
              </Layout>
            </PrivateRoute>
          } 
        />
        
        {/* Admin Policy Management */}
        <Route 
          path="/admin/policies" 
          element={
            <PrivateRoute>
              <Layout>
                <RoleRoute element={<PolicyManagementPage />} allowedRoles={['admin']} />
              </Layout>
            </PrivateRoute>
          } 
        />
        
        {/* Admin Leave Request Management */}
        <Route 
          path="/admin/leave-requests" 
          element={
            <PrivateRoute>
              <Layout>
                <RoleRoute element={<LeaveRequestManagementPage />} allowedRoles={['admin']} />
              </Layout>
            </PrivateRoute>
          } 
        />
        
        {/* Admin Email Test */}
        <Route 
          path="/admin/email-test" 
          element={
            <PrivateRoute>
              <Layout>
                <RoleRoute element={<EmailTestPage />} allowedRoles={['admin']} />
              </Layout>
            </PrivateRoute>
          } 
        />
        
        {/* Admin Monthly Report */}
        <Route 
          path="/admin/monthly-report" 
          element={
            <PrivateRoute>
              <Layout>
                <RoleRoute element={<AdminMonthlyReportPage />} allowedRoles={['admin']} />
              </Layout>
            </PrivateRoute>
          } 
        />
        
        {/* Public Email Test */}
        <Route 
          path="/email-test" 
          element={<EmailTestPage />}
        />
        
        {/* Manager Tasks */}
        <Route 
          path="/manager/tasks" 
          element={
            <PrivateRoute>
              <Layout>
                <RoleRoute element={<ManagerTasksPage />} allowedRoles={['admin', 'manager']} />
              </Layout>
            </PrivateRoute>
          } 
        />
        
        {/* Manager Project Tasks */}
        <Route 
          path="/manager/project-tasks" 
          element={
            <PrivateRoute>
              <Layout>
                <RoleRoute element={<ManagerProjectTasksPage />} allowedRoles={['admin', 'manager']} />
              </Layout>
            </PrivateRoute>
          } 
        />
        
        {/* Manager Projects */}
        <Route 
          path="/manager/projects" 
          element={
            <PrivateRoute>
              <Layout>
                <RoleRoute element={<ManagerProjectsPage />} allowedRoles={['admin', 'manager']} />
              </Layout>
            </PrivateRoute>
          } 
        />
        
        {/* Manager Team */}
        <Route 
          path="/manager/team" 
          element={
            <PrivateRoute>
              <Layout>
                <RoleRoute element={<ManagerTeamPage />} allowedRoles={['admin', 'manager']} />
              </Layout>
            </PrivateRoute>
          } 
        />
        
        {/* Manager Task Detail */}
        <Route 
          path="/manager/tasks/:taskId" 
          element={
            <PrivateRoute>
              <Layout>
                <RoleRoute element={<ManagerTaskDetailPage />} allowedRoles={['admin', 'manager']} />
              </Layout>
            </PrivateRoute>
          } 
        />
        
        {/* Profile Page - All Roles */}
        <Route 
          path="/profile" 
          element={
            <PrivateRoute>
              <Layout>
                <ProfilePage />
              </Layout>
            </PrivateRoute>
          } 
        />
        
        {/* Settings Page - All Roles */}
        <Route 
          path="/settings" 
          element={
            <PrivateRoute>
              <Layout>
                <SettingsPage />
              </Layout>
            </PrivateRoute>
          } 
        />
        
        {/* User Detail Page - Admin */}
        <Route 
          path="/admin/team/:userId" 
          element={
            <PrivateRoute>
              <Layout>
                <RoleRoute element={<UserDetailPage />} allowedRoles={['admin']} />
              </Layout>
            </PrivateRoute>
          } 
        />
        
        {/* Employee Task Detail */}
        <Route 
          path="/employee/tasks/:taskId" 
          element={
            <PrivateRoute>
              <Layout>
                <RoleRoute element={<EmployeeTaskDetailPage />} allowedRoles={['admin', 'manager', 'employee']} />
              </Layout>
            </PrivateRoute>
          } 
        />
        
        {/* Employee Tasks */}
        <Route 
          path="/employee/tasks" 
          element={
            <PrivateRoute>
              <Layout>
                <RoleRoute element={<EmployeeTasksPage />} allowedRoles={['admin', 'manager', 'employee']} />
              </Layout>
            </PrivateRoute>
          } 
        />
        
        {/* Employee Projects */}
        <Route 
          path="/employee/projects" 
          element={
            <PrivateRoute>
              <Layout>
                <RoleRoute element={<EmployeeProjectTasksPage />} allowedRoles={['admin', 'manager', 'employee']} />
              </Layout>
            </PrivateRoute>
          } 
        />
        
        {/* Employee Project Tasks Detail */}
        <Route 
          path="/employee/project-tasks" 
          element={
            <PrivateRoute>
              <Layout>
                <RoleRoute element={<ProjectTasksDetailPage />} allowedRoles={['admin', 'manager', 'employee']} />
              </Layout>
            </PrivateRoute>
          } 
        />
        
        {/* Employee Timesheet */}
        <Route 
          path="/employee/timesheet" 
          element={
            <PrivateRoute>
              <Layout>
                <RoleRoute element={<TimesheetPage />} allowedRoles={['admin', 'manager', 'employee']} />
              </Layout>
            </PrivateRoute>
          } 
        />
        
        {/* Employee Monthly Report */}
        <Route 
          path="/employee/monthly-report" 
          element={
            <PrivateRoute>
              <Layout>
                <RoleRoute element={<MonthlyReportPage />} allowedRoles={['admin', 'manager', 'employee']} />
              </Layout>
            </PrivateRoute>
          } 
        />
        
        {/* Employee Office Policy */}
        <Route 
          path="/employee/policy" 
          element={
            <PrivateRoute>
              <Layout>
                <RoleRoute element={<OfficePolicyPage />} allowedRoles={['admin', 'manager', 'employee']} />
              </Layout>
            </PrivateRoute>
          } 
        />
        
        {/* Time Tracking */}
        <Route 
          path="/time-tracking" 
          element={
            <PrivateRoute>
              <Layout>
                <RoleRoute element={<TimeTrackingPage />} allowedRoles={['admin', 'manager', 'employee']} />
              </Layout>
            </PrivateRoute>
          } 
        />
        
        {/* Task Detail Page */}
        <Route 
          path="/employee/tasks/:taskId" 
          element={
            <PrivateRoute>
              <Layout>
                <RoleRoute element={<TaskDetailPage />} allowedRoles={['admin', 'manager', 'employee']} />
              </Layout>
            </PrivateRoute>
          } 
        />
        
        {/* Task Comments Page - Redirect to Task Detail */}
        <Route 
          path="/employee/tasks/:taskId/comments" 
          element={
            <PrivateRoute>
              <Layout>
                <RoleRoute element={<TaskDetailPage />} allowedRoles={['admin', 'manager', 'employee']} />
              </Layout>
            </PrivateRoute>
          } 
        />
        
        {/* Legacy tasks route - redirects based on role */}
        <Route 
          path="/tasks" 
          element={
            <PrivateRoute>
              <Layout>
                {getCurrentUser()?.role === 'admin' ? 
                  <Navigate to="/admin/tasks" /> : 
                  getCurrentUser()?.role === 'manager' ? 
                    <Navigate to="/manager/tasks" /> : 
                    <Navigate to="/employee/tasks" />
                }
              </Layout>
            </PrivateRoute>
          } 
        />
        {/* Admin Team */}
        <Route 
          path="/admin/team" 
          element={
            <PrivateRoute>
              <Layout>
                <RoleRoute element={<AdminTeamPage />} allowedRoles={['admin']} />
              </Layout>
            </PrivateRoute>
          } 
        />
        
        {/* Admin Clients */}
        <Route 
          path="/admin/clients" 
          element={
            <PrivateRoute>
              <Layout>
                <RoleRoute element={<AdminClientsPage />} allowedRoles={['admin']} />
              </Layout>
            </PrivateRoute>
          } 
        />
        
        {/* Admin Projects */}
        <Route 
          path="/admin/projects" 
          element={
            <PrivateRoute>
              <Layout>
                <RoleRoute element={<ProjectsPage />} allowedRoles={['admin']} />
              </Layout>
            </PrivateRoute>
          } 
        />

        {/* Admin Project Overview */}
        <Route 
          path="/admin/project-overview" 
          element={
            <PrivateRoute>
              <Layout>
                <RoleRoute element={<ProjectOverviewPage />} allowedRoles={['admin']} />
              </Layout>
            </PrivateRoute>
          } 
        />
        
        {/* Admin Project Tasks */}
        <Route 
          path="/admin/projects/:projectId/tasks" 
          element={
            <PrivateRoute>
              <Layout>
                <RoleRoute element={<ProjectTasksPage />} allowedRoles={['admin']} />
              </Layout>
            </PrivateRoute>
          } 
        />
        
        {/* Project Tasks */}
        <Route 
          path="/admin/project-tasks" 
          element={
            <PrivateRoute>
              <Layout>
                <RoleRoute element={<ProjectTasksPage />} allowedRoles={['admin', 'manager', 'employee']} />
              </Layout>
            </PrivateRoute>
          } 
        />
        
        {/* Legacy team route - redirects based on role */}
        <Route 
          path="/team" 
          element={
            <PrivateRoute>
              <Layout>
                {getCurrentUser()?.role === 'admin' ? 
                  <Navigate to="/admin/team" /> : 
                  <TeamPage />
                }
              </Layout>
            </PrivateRoute>
          } 
        />
        
        {/* Legacy clients route - redirects based on role */}
        <Route 
          path="/clients" 
          element={
            <PrivateRoute>
              <Layout>
                {getCurrentUser()?.role === 'admin' ? 
                  <Navigate to="/admin/clients" /> : 
                  <ClientsPage />
                }
              </Layout>
            </PrivateRoute>
          } 
        />
        <Route path="/" element={<Navigate to="/login" replace />} />
        <Route path="/home" element={<Navigate to="/login" replace />} />
        </Routes>
        </TimeTrackingProvider>
      </NotificationProvider>
    </Router>
  );
}

export default App;